// Remembers which organization and Team the coach was looking at.
//
// Deliberately its own key rather than `authStore.activeOrgId`: the dashboard's
// switcher never changes the app-wide organization — looking at another
// organization's banner is not the same as switching the app into it — so
// persisting through the store would make a glance at one organization silently
// re-scope every other page.
//
// `null` is a real value on both fields ("All organizations", "All teams") and has
// to survive a round trip, which is why the absence of a stored scope is `null`
// for the whole record rather than a pair of nulls.

const STORAGE_KEY = 'rallypd.dashboard.scope'

export interface DashboardScope {
  /** null = all organizations. */
  orgId: number | null
  /** null = all Teams in that scope. */
  teamId: number | null
}

/** Reads the stored scope. Returns null when there is none, or when it is unreadable. */
export const readDashboardScope = (): DashboardScope | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DashboardScope>
    const orgId = typeof parsed.orgId === 'number' ? parsed.orgId : null
    const teamId = typeof parsed.teamId === 'number' ? parsed.teamId : null
    // A Team without its organization cannot be validated against anything, and
    // would silently widen to the wrong scope. Drop the pair.
    return { orgId, teamId: orgId === null ? null : teamId }
  } catch {
    // Private mode, cleared storage, or a value from an older shape. A forgotten
    // scope is a fine outcome; a dashboard that will not render is not.
    return null
  }
}

export const writeDashboardScope = (scope: DashboardScope): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scope))
  } catch {
    // Storage being unavailable costs the user a remembered selection, nothing more.
  }
}
